/**
 * Trusscore SlatWall accessories — the hooks, baskets and shelves a user can
 * hang on a SlatWall section in the 3D Designer.
 *
 * Each kind is a real product, drawn from its own STL (public/models/slatwall,
 * supplied by Trusscore; millimetres, x out from the wall, y up, z along the
 * wall) at its true size. The sizes here are those files' extents, in feet,
 * as `w` along the wall, `h` up and `d` out from the wall.
 *
 * An accessory stays on the wall by two TABS bent back from its plate, which
 * reach into two of the panel's grooves, 3" apart, and it hangs with the
 * bottom of its lower tab resting on the bottom edge of that groove's mouth.
 * `seat` gives, measured from the file, how high the lower tab's bottom is
 * above the model's bottom and how far the tabs reach behind the plate (the
 * model is pushed into the wall by that much, so the plate sits on the slats
 * and the tabs are inside the channels). The placement rules
 * (calculations/sections/accessories.ts) do the rest.
 *
 * NOT YET IN THE PRODUCTS TABLE: `sku` is the catalogue code from the website
 * and `productId` is empty. When the products are added with pricing, give
 * each kind its product id here and the bill of materials can price them;
 * nothing else needs to change. Until then the designer counts them by kind.
 */

export type SlatWallAccessoryKind = 'hook-single' | 'hook-double' | 'hook-bike' | 'hook-hose' | 'basket-18' | 'shelf-24';

/** How an accessory holds what staging puts on it. */
export type AccessoryHold =
  /** An item hangs from a point (a rake over a hook). */
  | 'hang'
  /** An item stands on a surface (bottles on a shelf). */
  | 'rest'
  /** An item sits inside (a bundle in a basket). */
  | 'contain';

export interface SlatWallAccessoryDef {
  kind: SlatWallAccessoryKind;
  /** Trusscore catalogue code. */
  sku: string;
  label: string;
  /** The STL, under public/models/slatwall. */
  model: string;
  /** Feet: along the wall, up, out from the wall. */
  size: { w: number; h: number; d: number };
  /** How it seats (feet): the lower tab's bottom above the model's bottom, and how far the tabs reach behind the plate. */
  seat: { tabBottom: number; tabDepth: number };
  hold: AccessoryHold;
  /**
   * Where a staged item is held, from the accessory's bottom-left corner on
   * the wall (feet): along, up, out. A hook's prong, a shelf's top, a basket's floor.
   */
  holdAt: { x: number; y: number; z: number };
  /**
   * The file was modelled with its wall side at x = max rather than x = 0
   * (the 18" basket): the loader turns it the other way about.
   */
  flipDepth?: boolean;
  /** The product id once the accessory is in the products table (pricing). */
  productId?: string;
}

const MM = 1 / 304.8;
/** Every model's tabs reach the same 12.5 mm behind its plate. */
const TAB_DEPTH = 12.5 * MM;

export const SLATWALL_ACCESSORIES: SlatWallAccessoryDef[] = [
  {
    kind: 'hook-single', sku: 'CRO.S6', label: '6" Single hook', model: 'Single_hook.STL',
    size: { w: 76.5 * MM, h: 92.7 * MM, d: 160.7 * MM },
    seat: { tabBottom: 0, tabDepth: TAB_DEPTH },
    hold: 'hang', holdAt: { x: 76.5 * MM / 2, y: 92.7 * MM * 0.45, z: 160.7 * MM * 0.7 },
  },
  {
    kind: 'hook-double', sku: 'CRO.D6', label: '6" Double hook', model: 'Double_hook.STL',
    size: { w: 76.5 * MM, h: 92.7 * MM, d: 160.6 * MM },
    seat: { tabBottom: 0, tabDepth: TAB_DEPTH },
    hold: 'hang', holdAt: { x: 76.5 * MM / 2, y: 92.7 * MM * 0.45, z: 160.6 * MM * 0.7 },
  },
  {
    kind: 'hook-bike', sku: 'CRO.V', label: 'Bike hook', model: 'Single_hook2.STL',
    size: { w: 76.5 * MM, h: 130.2 * MM, d: 192.4 * MM },
    seat: { tabBottom: 1.1 * MM, tabDepth: TAB_DEPTH },
    hold: 'hang', holdAt: { x: 76.5 * MM / 2, y: 130.2 * MM * 0.4, z: 192.4 * MM * 0.7 },
  },
  {
    kind: 'hook-hose', sku: 'CRO.H', label: 'Hose hook', model: 'Triangle_hook.STL',
    size: { w: 164.0 * MM, h: 123.1 * MM, d: 184.8 * MM },
    seat: { tabBottom: 0, tabDepth: TAB_DEPTH },
    hold: 'hang', holdAt: { x: 164.0 * MM / 2, y: 123.1 * MM * 0.5, z: 184.8 * MM * 0.6 },
  },
  {
    kind: 'basket-18', sku: 'CRO.P18', label: '18" Basket', model: 'Basket1.STL',
    size: { w: 472.6 * MM, h: 220.4 * MM, d: 331.1 * MM },
    seat: { tabBottom: 127.7 * MM, tabDepth: TAB_DEPTH },
    hold: 'contain', holdAt: { x: 472.6 * MM / 2, y: 0.06, z: 331.1 * MM / 2 },
    flipDepth: true,
  },
  {
    kind: 'shelf-24', sku: 'CRO.T24', label: '24" Shelf', model: 'Basket2.STL',
    size: { w: 605.0 * MM, h: 115.9 * MM, d: 321.5 * MM },
    seat: { tabBottom: 25.2 * MM, tabDepth: TAB_DEPTH },
    hold: 'rest', holdAt: { x: 605.0 * MM / 2, y: 0.07, z: 321.5 * MM / 2 },
  },
];

const BY_KIND = new Map(SLATWALL_ACCESSORIES.map((a) => [a.kind, a]));

export function accessoryDef(kind: SlatWallAccessoryKind): SlatWallAccessoryDef {
  const def = BY_KIND.get(kind);
  if (!def) throw new Error(`Unknown SlatWall accessory: ${kind}`);
  return def;
}

export const isAccessoryKind = (v: unknown): v is SlatWallAccessoryKind => typeof v === 'string' && BY_KIND.has(v as SlatWallAccessoryKind);

/** Where the model files are served from. */
export const ACCESSORY_MODEL_PATH = `${process.env.PUBLIC_URL || ''}/models/slatwall/`;
